import React, { useState, useEffect } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { db } from '../firebase/config';
import { doc, updateDoc, getDoc, collection, addDoc, serverTimestamp } from 'firebase/firestore';

const ReactionButton = ({ post, reactionType }) => {
    const { currentUser, userProfile } = useAuth();
    const [count, setCount] = useState(0);
    const [userHasReacted, setUserHasReacted] = useState(false);

    const reactionEmojis = {
        fire: '🔥',
        mindblown: '🤯',
        funny: '😂',
        wholesome: '❤️',
        relatable: '😢'
    };

    useEffect(() => {
        if (!post) return;
        const reactions = post.reactions || {};
        const reactors = reactions[reactionType] || [];
        setCount(reactors.length);
        if (currentUser) {
            setUserHasReacted(reactors.includes(currentUser.uid));
        }
    }, [post, reactionType, currentUser]);

    const handleReaction = async () => {
        if (!currentUser || !post?.id) return;
        const postRef = doc(db, 'posts', post.id);

        const hadAlreadyReacted = userHasReacted;
        const newHasReacted = !hadAlreadyReacted;
        const newCount = hadAlreadyReacted ? Math.max(0, count - 1) : count + 1;

        // Optimistic UI update
        setUserHasReacted(newHasReacted);
        setCount(newCount);

        try {
            const postSnap = await getDoc(postRef);
            if (!postSnap.exists()) return;

            const postData = postSnap.data() || {};
            const reactions = { ...(postData.reactions || {}) };

            // Ensure every reaction key is initialized as an array
            Object.keys(reactionEmojis).forEach(key => {
                reactions[key] = Array.isArray(reactions[key]) ? [...reactions[key]] : [];
                // Remove user from all reaction types first
                reactions[key] = reactions[key].filter(uid => uid !== currentUser.uid);
            });

            // If toggling on, add user to selected reactionType
            if (newHasReacted) {
                reactions[reactionType].push(currentUser.uid);
            }

            await updateDoc(postRef, { reactions });

            // Optional Notification for post author (silently catch any notification error so reaction never fails)
            if (newHasReacted && post.authorId && post.authorId !== currentUser.uid) {
                try {
                    const senderName = userProfile?.displayName || currentUser.displayName || 'Someone';
                    const textSnippet = (post.text || '').slice(0, 50);
                    const notifRef = collection(db, 'users', post.authorId, 'notifications');
                    addDoc(notifRef, {
                        type: 'reaction',
                        reactionType: reactionType,
                        fromUserId: currentUser.uid,
                        fromUserName: senderName,
                        postId: post.id,
                        postTextSnippet: textSnippet,
                        createdAt: serverTimestamp(),
                        read: false
                    }).catch(() => {});
                } catch (_) {}
            }

        } catch (e) {
            console.error("Reaction update failed:", e);
            // Revert optimistic update on failure
            setUserHasReacted(hadAlreadyReacted);
            setCount(hadAlreadyReacted ? count : Math.max(0, count - 1));
        }
    };

    return (
        <button
            onClick={handleReaction}
            className={`flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-semibold transition-all duration-150 active:scale-95 ${
                userHasReacted
                    ? 'bg-sky-500/15 dark:bg-sky-500/20 text-sky-600 dark:text-sky-400 border border-sky-500/30'
                    : 'bg-slate-100 dark:bg-slate-800/80 hover:bg-slate-200 dark:hover:bg-slate-700/80 text-slate-600 dark:text-slate-300 border border-slate-200/60 dark:border-slate-700/60'
            }`}
        >
            <span className="text-sm sm:text-base leading-none">{reactionEmojis[reactionType]}</span>
            <span className="text-xs font-semibold leading-none">{count}</span>
        </button>
    );
};

export default ReactionButton;